import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '@/lib/capacitor';
import {
  ArrowLeft,
  Bell,
  Send,
  User,
  Smartphone,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';

interface PushUser {
  _id: string;
  name: string;
  email?: string;
  deviceCount: number;
  lastTokenUpdate: string;
}

export default function PushPage() {
  const [users, setUsers] = useState<PushUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pushConfigured, setPushConfigured] = useState(false);

  // Nudge dialog state
  const [nudgeTarget, setNudgeTarget] = useState<PushUser | null>(null);
  const [nudgeMessage, setNudgeMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchUsers();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/push/status`);
      const data = await response.json();
      if (data.success) {
        setPushConfigured(data.configured);
      }
    } catch (error) {
      console.error('Failed to fetch push status:', error);
    }
  };

  const fetchUsers = async (showRefreshState = false) => {
    if (showRefreshState) setIsRefreshing(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/push/users`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchUsers(true);
  };

  const openNudgeDialog = (user: PushUser) => {
    setNudgeTarget(user);
    setNudgeMessage('');
  };

  const closeNudgeDialog = () => {
    setNudgeTarget(null);
    setNudgeMessage('');
  };

  const handleSendNudge = async () => {
    if (!nudgeTarget || !nudgeMessage.trim()) return;

    setIsSending(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/push/nudge/${nudgeTarget._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: nudgeMessage.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        // Show success with details about sent/failed
        let description = data.message;
        if (data.failed > 0) {
          description += ` (${data.failed} device${data.failed > 1 ? 's' : ''} failed - may have been uninstalled)`;
        }
        toast({
          title: 'Nudge sent!',
          description,
        });
        closeNudgeDialog();
      } else {
        toast({
          title: 'Failed to send',
          description: data.message || 'Something went wrong',
          variant: 'destructive',
        });
      }

      // Refresh the user list to reflect any removed devices
      // This will update device counts if any tokens were invalidated
      await fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send nudge',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Bell className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">Push Notifications</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Status Banner */}
        {!pushConfigured && (
          <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-500">Push notifications not configured</p>
              <p className="text-sm text-muted-foreground mt-1">
                Set up Firebase credentials to enable push notifications.
                See the setup documentation for details.
              </p>
            </div>
          </div>
        )}

        {users.length === 0 ? (
          <div className="text-center py-12">
            <Smartphone className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No users with registered devices</p>
            <p className="text-sm text-muted-foreground mt-2">
              Users need to open the app to register their devices for push notifications.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="divide-y divide-border">
              {users.map((user) => (
                <div
                  key={user._id}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user.name}</p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Smartphone className="w-3 h-3" />
                        {user.deviceCount} device{user.deviceCount !== 1 ? 's' : ''}
                      </span>
                      <span className="hidden sm:inline">
                        Last active: {formatDate(user.lastTokenUpdate)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openNudgeDialog(user)}
                    disabled={!pushConfigured}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Nudge
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Nudge Dialog */}
      <Dialog open={!!nudgeTarget} onOpenChange={() => closeNudgeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Nudge to {nudgeTarget?.name}</DialogTitle>
            <DialogDescription>
              Send a push notification to {nudgeTarget?.name}'s{' '}
              {nudgeTarget?.deviceCount === 1
                ? 'device'
                : `${nudgeTarget?.deviceCount} devices`}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="message" className="text-sm font-medium">
                Message
              </label>
              <Input
                id="message"
                placeholder="Hey! Check out the display..."
                value={nudgeMessage}
                onChange={(e) => setNudgeMessage(e.target.value)}
                disabled={isSending}
                maxLength={500}
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-right">
                {nudgeMessage.length}/500
              </p>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isSending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleSendNudge}
              disabled={isSending || !nudgeMessage.trim()}
            >
              {isSending ? 'Sending...' : 'Send Nudge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
